import { reactive, computed, ref, watch } from 'vue'
import type { ZodType } from 'zod'

import { isPlainObject } from './plainObject'

/**
 * Errors keyed by form field. Known fields autocomplete; a server-only key that has
 * no matching field is still allowed through, so nothing is silently swallowed.
 */
export type FormErrors<T extends object = Record<string, never>> = Partial<
  Record<keyof T | (string & {}), string>
>

type TouchedFields<T> = Partial<Record<keyof T, boolean>>

type ZodIssueLike = { path: PropertyKey[]; message: string }

/** The field an error belongs to — `config.htmlTemplate` is an error of `config`. */
const rootOf = (path: string) => path.split('.')[0] ?? path

/**
 * Every path the form can carry an error for: its own keys, plus the keys of the plain
 * objects nested in them (`malwareScanConfigurationAttributes.countries`). Arrays are
 * left whole — an API error names an attribute, never a row of one, and a form that
 * edits a list shows the message on the list.
 */
function fieldPaths(value: object, prefix = ''): string[] {
  return Object.entries(value).flatMap(([key, child]) => {
    const path = prefix ? `${prefix}.${key}` : key

    return isPlainObject(child) ? [path, ...fieldPaths(child, path)] : [path]
  })
}

/**
 * Zod's own issues rather than `flatten()`, which keeps the first segment of a path and
 * throws the rest away, leaving a nested form one error for a whole block and no way to
 * say which control it is about.
 *
 * Each issue is recorded twice: under its full path (`config.htmlTemplate`), which is
 * what a nested field binds, and under its root, which is what a block-level `FormItem`
 * and `useErrorSections` already read. First message per key wins, so a rule written
 * about the block itself still beats one from inside it.
 */
function parseZodErrors<T extends object>(error: {
  issues: ZodIssueLike[]
}): FormErrors<T> {
  const result: Record<string, string> = {}

  for (const issue of error.issues) {
    const path = issue.path.map(String).join('.')

    // A refinement that names no path is about the form as a whole; there is no field
    // to pin it to.
    if (!path) continue

    if (!(path in result)) result[path] = issue.message

    const root = rootOf(path)
    if (!(root in result)) result[root] = issue.message
  }

  return result as FormErrors<T>
}

/**
 * The state, validation and errors of one entity form — an entity's `model/form.ts` is this
 * call, and the SFC over it binds fields and nothing else.
 *
 * Hand it `initial()` (a factory, so each modal gets its own state), the zod `schema` and
 * `buildPayload`. What comes back is the reactive `state`, `errors` for the `FormItem`s,
 * `formErrors` for the messages that belong to no field, and the
 * `validateForm`/`setServerErrors` pair the submit pipeline drives — pass the whole result to `useApiForm`
 * or `useModalForm` rather than calling those by hand.
 *
 * Errors clear themselves as the reader answers them, and a rule is never raised about a
 * field they have not touched yet.
 *
 * `P = unknown`, not `any`: the payload type is normally inferred from `buildPayload`, so
 * the default is rarely reached — and when inference does fail, `unknown` makes that
 * visible at the call site instead of switching checking off silently.
 */
export function useEntityForm<T extends object, P = unknown>(config: {
  initial: () => T
  buildPayload: (form: T) => P
  /**
   * `ZodType`, not the deprecated alias the installed zod still exports for v3 compatibility.
   */
  schema?: ZodType<T>
  validate?: (form: T) => FormErrors<T> | null
  clearErrorsOnChange?: boolean
  /**
   * Server error codes that name no field of this form — a rejected row of a list the form
   * holds as one control, say. Without the alias the message falls through to `formErrors`,
   * where it reads as a complaint about the form rather than about the control that caused it.
   */
  errorAliases?: Record<string, keyof T & string>
}) {
  // `Reactive<T>` cannot be indexed by `keyof T`, and every form here is a flat
  // data object, so the proxy is described by the shape it actually carries.
  const state = reactive(config.initial()) as T

  const clientErrors = ref<FormErrors<T>>({})
  const serverErrors = ref<FormErrors<T>>({})
  const touched = ref<TouchedFields<T>>({})

  /**
   * How many times the form has been submitted. `validateForm` is what a submit runs
   * first (`useApiForm.submit`), so counting there counts attempts.
   *
   * It exists because pressing Save is a question, and the answer has to be visible
   * even when it is the same answer as last time: a section that hides a rejected
   * field is reopened on every attempt, not only on the attempt that first rejected
   * it. See `useErrorSection`.
   */
  const submitAttempts = ref(0)

  /**
   * Field paths, so `useApiForm` can map API error codes back onto the form — the nested
   * ones included, because the API words a block's attribute in
   * full (`malware_scan_configuration_attributes.countries`).
   */
  const fields = fieldPaths(state as object)

  /**
   * Also clears the errors of whatever the field holds: replacing a block leaves the
   * complaints about its innards behind, and the proxy below only ever sees the block.
   */
  function clearError(field: keyof T | (string & {})) {
    const prefix = `${String(field)}.`

    for (const store of [clientErrors, serverErrors]) {
      for (const key of Object.keys(store.value)) {
        if (key === field || key.startsWith(prefix)) {
          delete store.value[key as keyof T]
        }
      }
    }
  }

  const form = new Proxy(state, {
    set(target, prop, value) {
      const key = prop as keyof T
      target[key] = value

      if (config.clearErrorsOnChange !== false) {
        clearError(key)
      }

      return true
    },
    get(target, prop) {
      return target[prop as keyof T]
    }
  }) as T

  function getValidationErrors(): FormErrors<T> {
    let zodErrors: FormErrors<T> = {}
    let customErrors: FormErrors<T> = {}

    if (config.schema) {
      const result = config.schema.safeParse(state)
      if (!result.success) {
        zodErrors = parseZodErrors<T>(result.error)
      }
    }

    if (config.validate) {
      const result = config.validate(state)
      if (result) {
        customErrors = result
      }
    }

    return {
      ...zodErrors,
      ...customErrors
    }
  }

  function validateForm() {
    submitAttempts.value++

    const errs = getValidationErrors()
    clientErrors.value = errs

    for (const key of Object.keys(state) as Array<keyof T>) {
      touched.value[key] = true
    }

    return Object.keys(errs).length === 0
  }

  /**
   * Drop the complaints the reader has already answered.
   *
   * The proxy below clears the error of whatever field is assigned, but it only ever sees
   * the **top level**: a control bound to `config.htmlTemplate` writes straight into the
   * nested object, so nothing there would clear until the next Save. This watch is what
   * covers the nested paths, so no field component has to call `clearError` itself.
   *
   * Deleting keys rather than reassigning is the important part: `validateForm` gives
   * `clientErrors` a **fresh object** on every attempt, which is what `useErrorSections`
   * leans on to reopen a folded section on a repeated Save. Mutating in place leaves
   * that alone.
   *
   * Only ever removes. A rule that starts failing while the reader types is not raised
   * until they ask, which is what `touched` is about everywhere else in this file.
   */
  watch(
    () => state,
    () => {
      const current = clientErrors.value
      const keys = Object.keys(current)
      if (!keys.length) return

      const stillFailing = getValidationErrors()

      for (const key of keys) {
        if (!(key in stillFailing)) delete current[key as keyof T]
      }
    },
    { deep: true }
  )

  function validateField(field: keyof T | (string & {})) {
    touched.value[rootOf(String(field)) as keyof T] = true

    const errs = getValidationErrors()
    const fieldError = errs[field as keyof T]

    if (fieldError) {
      clientErrors.value[field as keyof T] = fieldError
    } else {
      delete clientErrors.value[field as keyof T]
    }

    return !fieldError
  }

  function markTouched(field: keyof T) {
    touched.value[field] = true
  }

  const errors = computed(() => {
    const visibleClientErrors = {} as FormErrors<T>

    // Touched is tracked per field, and a nested path belongs to its field: nothing
    // ever marks `config.htmlTemplate` touched, so asking about it directly would
    // hide every error inside a block.
    for (const key in clientErrors.value) {
      if (touched.value[rootOf(key) as keyof T]) {
        visibleClientErrors[key as keyof T] = clientErrors.value[key as keyof T]
      }
    }

    return {
      ...visibleClientErrors,
      ...serverErrors.value
    }
  })

  /**
   * Server errors that belong to no field — a rejected combination, a generic
   * `system` code. They have nowhere to render inside a `FormItem`, so the modal
   * shows them once above the form instead of pinning them to an arbitrary field.
   */
  const formErrors = computed(() =>
    Object.entries(serverErrors.value)
      .filter(([key]) => !fields.includes(key))
      .map(([, message]) => message as string)
  )

  function resetForm() {
    Object.assign(state, config.initial())
    clientErrors.value = {}
    serverErrors.value = {}
    touched.value = {}
  }

  function setValues(values: Partial<T>) {
    Object.entries(values).forEach(([key, value]) => {
      // `Object.entries` widens the key to `string` and the value to `unknown`, so the
      // pair has to be narrowed back onto `T` here; `values` is already `Partial<T>`,
      // which is what makes the assertion sound.
      form[key as keyof T] = value as T[keyof T]
    })
  }

  function buildPayload() {
    return config.buildPayload(state as T)
  }

  function setServerErrors(e: Record<string, string>) {
    serverErrors.value = e as FormErrors<T>
  }

  function clearServerErrors() {
    serverErrors.value = {}
  }

  function clearServerError(field: keyof T) {
    delete serverErrors.value[field]
  }

  function clearErrors() {
    clientErrors.value = {}
    serverErrors.value = {}
  }

  return {
    form,
    fields,
    errorAliases: config.errorAliases,
    errors,
    formErrors,
    touched,
    submitAttempts,

    resetForm,
    setValues,
    buildPayload,

    validateForm,
    validateField,
    markTouched,

    setServerErrors,
    clearServerErrors,
    clearServerError,
    clearErrors,
    clearError
  }
}

/**
 * The errors of one nested block, keyed the way that block names its own fields —
 * `malwareScanConfigurationAttributes.countries` reaches the scan config as
 * `countries`, which is what the component that draws it knows about.
 *
 * The block's own error (the bare `prefix` key) is left behind: it belongs to the
 * `FormItem` that holds the block, not to a control inside it.
 */
export function pickNestedErrors<T extends object>(
  errors: FormErrors<T>,
  prefix: string
): FormErrors {
  const head = `${prefix}.`
  const nested: Record<string, string> = {}

  for (const [key, message] of Object.entries(errors)) {
    if (message && key.startsWith(head))
      nested[key.slice(head.length)] = message
  }

  return nested
}
